import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/authContext';
import { changePassword } from '../../services/companiesService';
import { ActionLoader } from '../loader';
import { Banner } from '../common/Banner';
export default function SettingsProfile() {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, logout } = useAuth();
  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('La contraseña actual es obligatoria.'),
    newPassword: Yup.string()
      .required('La nueva contraseña es obligatoria.')
      .min(8, 'Debe tener al menos 8 caracteres.')
      .matches(/[a-z]/, 'Debe contener al menos una minúscula.')
      .matches(/[A-Z]/, 'Debe contener al menos una mayúscula.')
      .matches(/\d/, 'Debe contener al menos un número.')
      .matches(/[^A-Za-z0-9]/, 'Debe incluir al menos un carácter especial.')
      .notOneOf([Yup.ref('currentPassword')], 'La nueva contraseña debe ser diferente a la actual.'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden.')
      .required('Debes confirmar tu nueva contraseña.'),
  });

  const handleSubmit = async (values, { resetForm }) => {
    try{
        const confirmation = await Swal.fire({
            title: '¿Estás seguro?',
            text: "¡No podrás revertir este cambio!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, cambiarla!',
            cancelButtonText: 'Cancelar'
        });
        if (!confirmation.isConfirmed) return;
        setIsSubmitting(true);
        values.rut = user.rut;
        const response = await changePassword(values,user);
        setIsSubmitting(false);
        if (response.success) {
            Swal.fire({
                title: '¡Éxito!',
                text: 'Tu contraseña ha sido actualizada.',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });
            logout();
        }
        else {
            Swal.fire({
                title: 'Error',
                text: response.message,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    }catch (error) {
        console.error('Error al cambiar la contraseña:', error);
        Swal.fire({
            title: 'Error',
            text: 'Ocurrió un error al cambiar la contraseña.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
        });
    }
    resetForm();
  };

  return (
    <div className="w-full mx-auto font-sans">
      {/* Encabezado */}
      <Banner title="Cambio de contraseña" message="Utilice siempre contraseñas seguras."/>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Consejos de Seguridad */}
        <div className="bg-gray-50 p-6 rounded-lg w-full md:w-2/5 border border-gray-200">
          <h2 className="text-lg font-bold mb-4">Consejos de Seguridad</h2>
          <ul className="space-y-2 mb-6 text-sm">
            <li>✓ Al menos 8 caracteres</li>
            <li>✓ Mayúsculas y minúsculas</li>
            <li>✓ Números</li>
            <li>✓ Caracteres especiales</li>
            <li>✓ Distinta a contraseñas anteriores</li>
          </ul>
          <ul className="text-sm text-red-600">
            <li>✕ No usar información personal</li>
            <li>✕ No usar palabras comunes o secuencias</li>
            <li>✕ No reutilizar contraseñas</li>
          </ul>
        </div>

        {/* Formulario con Formik */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg w-full md:w-3/5">
          <h2 className="text-lg font-bold mb-4 flex items-center">🔒 Actualizar Contraseña</h2>
          <p className="text-gray-600 mb-6">Crea una nueva contraseña segura para proteger tu cuenta.</p>

          <Formik
            initialValues={{
              currentPassword: '',
              newPassword: '',
              confirmPassword: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty }) => (
              <Form className="space-y-6">
                {/* Contraseña actual */}
                <div>
                  <label className="block mb-2 font-medium">Contraseña Actual</label>
                  <div className="relative">
                    <Field
                      type={showCurrent ? 'text' : 'password'}
                      name="currentPassword"
                      className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                      placeholder="Ingresa tu contraseña actual"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent(!showCurrent)}
                      className="absolute right-3 top-3 text-gray-500"
                    >
                      {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <ErrorMessage name="currentPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Nueva contraseña */}
                <div>
                  <label className="block mb-2 font-medium">Nueva Contraseña</label>
                  <div className="relative">
                    <Field
                      type={showNew ? 'text' : 'password'}
                      name="newPassword"
                      className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                      placeholder="Ingresa tu nueva contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-3 top-3 text-gray-500"
                    >
                      {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <ErrorMessage name="newPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Confirmar contraseña */}
                <div>
                  <label className="block mb-2 font-medium">Confirmar Nueva Contraseña</label>
                  <div className="relative">
                    <Field
                      type={showConfirm ? 'text' : 'password'}
                      name="confirmPassword"
                      className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                      placeholder="Confirma tu nueva contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-3 text-gray-500"
                    >
                      {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <button
                  type="submit"
                  disabled={!(isValid && dirty)}
                  className={`w-full text-white p-3 rounded-lg font-medium flex justify-center items-center bg-gradient-to-b from-[#09274A] to-[#0488AF] ${
                    !(isValid && dirty) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="mr-2">🔒</span>
                  Actualizar Contraseña
                </button>

                <p className="mt-4 text-gray-600 text-sm">
                  Por seguridad, serás desconectado después de <span className="text-blue-600">cambiar tu contraseña</span>.
                </p>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <ActionLoader open={isSubmitting} onClose={()=>{}}></ActionLoader>
    </div>
  );
}
